/**
 * Custom Hook for Confirmation Dialogs
 * Provides a clean interface for managing confirmation dialogs across components
 */

import { useState, useCallback } from 'react';

const useConfirmationDialog = () => {
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    type: 'default',
    claimData: {},
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    severity: 'medium',
    isLoading: false,
    onConfirm: null,
    onCancel: null
  });

  // Show confirmation dialog
  const showConfirmation = useCallback(({
    type = 'default',
    claimData = {},
    title,
    message, 
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    severity = 'medium',
    onConfirm,
    onCancel
  }) => {
    setDialogState({
      isOpen: true,
      type,
      claimData,
      title,
      message,
      confirmText,
      cancelText, 
      severity,
      isLoading: false,
      onConfirm,
      onCancel
    });
  }, []);

  // Hide confirmation dialog
  const hideConfirmation = useCallback(() => {
    if (dialogState.isLoading) return; // Prevent closing during operation
    
    setDialogState(prev => ({
      ...prev,
      isOpen: false,
      isLoading: false
    }));
  }, [dialogState.isLoading]);

  // Set loading state during async operations
  const setLoading = useCallback((loading) => {
    setDialogState(prev => ({
      ...prev,
      isLoading: loading
    }));
  }, []);

  // Handle confirm action
  const handleConfirm = useCallback(async () => {
    if (!dialogState.onConfirm || dialogState.isLoading) return;

    try {
      setLoading(true);
      await dialogState.onConfirm();
      hideConfirmation();
    } catch (error) {
      console.error('Confirmation action failed:', error);
      setLoading(false);
      // Don't close dialog on error - let parent component handle it
      throw error;
    }
  }, [dialogState.onConfirm, dialogState.isLoading, setLoading, hideConfirmation]);

  // Handle cancel action
  const handleCancel = useCallback(() => {
    if (dialogState.isLoading) return;

    if (dialogState.onCancel) {
      dialogState.onCancel();
    }
    hideConfirmation();
  }, [dialogState.isLoading, dialogState.onCancel, hideConfirmation]);

  // Shorthand methods for common dialog types
  const confirmAssign = useCallback((claimData, onConfirm) => {
    showConfirmation({
      type: 'assign',
      claimData,
      onConfirm
    });
  }, [showConfirmation]);

  const confirmRelease = useCallback((claimData, onConfirm) => {
    showConfirmation({
      type: 'release', 
      claimData,
      onConfirm
    });
  }, [showConfirmation]);

  const confirmBulkAssign = useCallback((claimData, onConfirm) => {
    showConfirmation({
      type: 'bulk_assign',
      claimData,
      onConfirm
    });
  }, [showConfirmation]);

  return {
    // State
    dialogState,
    
    // Actions
    showConfirmation,
    hideConfirmation,
    setLoading,
    handleConfirm,
    handleCancel,
    
    // Shorthand methods
    confirmAssign,
    confirmRelease,
    confirmBulkAssign
  };
};

export default useConfirmationDialog;